import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SOLUTIONS } from "@/data/site";
import { SectionHead, Reveal } from "@/components/common/Reveal";
import { SolutionUI } from "@/components/home/SolutionUI";

const Copy = ({ s }) => (
  <div className="space-y-3 pb-6 pl-12 pr-2 text-[15px] leading-relaxed">
    <p className="text-slate-400"><span className="font-mono text-[12px] uppercase tracking-widest text-rose-200/90">Problem </span>{s.problem}</p>
    <p className="text-slate-200"><span className="font-mono text-[12px] uppercase tracking-widest text-cyan-300">Approach </span>{s.approach}</p>
  </div>
);

const Item = ({ s, i, active, onSelect }) => (
  <li className="border-b border-white/10">
    <h3>
      <button type="button" onClick={onSelect} onMouseEnter={onSelect} aria-expanded={active} data-testid={`solution-item-${i}`}
        className="group flex w-full items-center gap-5 py-6 text-left">
        <span className={`font-mono text-[12px] transition-colors ${active ? "text-cyan-300" : "text-slate-400"}`}>0{i + 1}</span>
        <span className={`flex-1 text-lg font-bold tracking-tight transition-colors duration-300 sm:text-xl ${active ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`}>{s.title}</span>
        <Plus className={`h-5 w-5 shrink-0 transition-transform duration-300 ${active ? "rotate-45 text-cyan-300" : "text-slate-400"}`} />
      </button>
    </h3>
    <AnimatePresence initial={false}>
      {active && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
          <Copy s={s} />
          <div className="pb-8 lg:hidden"><SolutionUI type={s.ui} /></div>
        </motion.div>
      )}
    </AnimatePresence>
  </li>
);

export const Solutions = ({ index = "02" }) => {
  const [active, setActive] = useState(0);
  const cur = SOLUTIONS[active];
  return (
    <section id="solutions" className="grain relative scroll-mt-20 overflow-hidden bg-[#050b14] py-24 text-white lg:py-32" data-testid="solutions-section">
      <div className="grid-dark pointer-events-none absolute inset-0 opacity-60 [mask-image:linear-gradient(to_bottom,transparent,black_30%,black_70%,transparent)]" />
      <div className="container-q relative">
        <SectionHead dark index={index} label="Solutions by business need" title="Practical software for real operational problems."
          text="Most projects start with a specific frustration. These are the challenges we are most often asked to solve." />
        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <ul className="border-t border-white/10">
              {SOLUTIONS.map((s, i) => <Item key={s.id} s={s} i={i} active={active === i} onSelect={() => setActive(i)} />)}
            </ul>
          </Reveal>
          <div className="hidden lg:col-span-6 lg:block">
            <div className="sticky top-28">
              <div className="relative min-h-[380px]" data-testid="solution-preview">
                <AnimatePresence initial={false}>
                  <motion.div key={cur.id} initial={{ opacity: 0, y: 20, rotateX: 6 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-x-0 top-0">
                    <SolutionUI type={cur.ui} />
                  </motion.div>
                </AnimatePresence>
              </div>
              <p className="mt-4 font-mono text-[12px] text-slate-400">Illustrative interface · sample data</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
